import { useContext, useRef } from "react";
import { Image } from "@chakra-ui/react";
import { AuthContext } from "contexts/AuthContext";
import TypeAnimation from "../../TypeAnimation";
import arrow from '../../assets/arrow-narrow-right.svg'


// Debounce function to limit the rate at which a function can fire
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

const InputArea = ({
                       promptValue,
                       setPromptValue,
                       handleSubmit,
                       handleSecondary,
                       disabled,
                       reset,
                       isStopped,
                   }) => {

    const { state } = useContext(AuthContext);
    const openai_keys = state.profile?.domain?.settings?.openai_keys || {};
    const OPENAI_API_KEY = Object.values(openai_keys)?.length ? Object.values(openai_keys)[0] : '';
    const hasOpenAiKey = Boolean(OPENAI_API_KEY);

    const placeholder = handleSecondary
        ? "Any questions about this report?"
        : "What would you like to research next?";

    const textareaRef = useRef(null);

    const resetHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = '3em'; // Reset to base height
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                // Allow new line on Shift+Enter
            } else {
                e.preventDefault();
                if (!disabled) {
                    if (reset) reset();
                    handleSubmit(promptValue);
                    setPromptValue(''); // Clear prompt value
                    resetHeight(); // Reset height after submit
                }
            }
        }
    };

    // Debounced version of the height adjustment function
    const adjustHeight = debounce((target) => {
        target.style.height = 'auto'; // Reset height to auto to allow shrinking
        target.style.height = `${target.scrollHeight}px`; // Adjust height
    }, 100); // Adjust the delay as needed

    const handleTextareaChange = (e) => {
        const target = e.target;
        adjustHeight(target); // Use debounced function
        setPromptValue(target.value);
    };

    // Only show input if not stopped
    if (isStopped) {
        return null;
    }

    return (
        <>
            {!hasOpenAiKey && (
                <span className="text-left w-full block text-red-500">The search is unavailable. The OpenAI key is missing</span>
            )}

            <form
                className="mx-auto flex w-full items-center justify-between rounded-lg border bg-white px-3 shadow-[2px_2px_38px_0px_rgba(0,0,0,0.25),0px_-2px_4px_0px_rgba(0,0,0,0.25)_inset,1px_2px_4px_0px_rgba(0,0,0,0.25)_inset]"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (reset) reset();
                    handleSubmit(promptValue);
                    setPromptValue(''); // Clear prompt value
                    resetHeight();
                }}
            >

                <input
                    placeholder={placeholder}
                    ref={textareaRef}
                    className="focus-visible::outline-0 my-1 w-full pl-5 font-medium not-italic leading-[normal]
                        text-[#1B1B16]/30 text-black outline-none focus-visible:ring-0 focus-visible:ring-offset-0
                        sm:text-xl min-h-[3em] resize-none"
                    disabled={disabled || !hasOpenAiKey}
                    value={promptValue}
                    required
                    rows={2}
                    onKeyDown={handleKeyDown}
                    onChange={handleTextareaChange}
                />

                <button
                    disabled={disabled || !hasOpenAiKey}
                    type="submit"
                    className={"relative flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-[3px] " +
                        "bg-[linear-gradient(154deg,#1B1B16_23.37%,#565646_91.91%)] disabled:pointer-events-none " +
                        "disabled:opacity-75 " + (disabled || !hasOpenAiKey ? 'cursor-not-allowed' : 'cursor-pointer')}
                >
                    {disabled && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <TypeAnimation/>
                        </div>
                    )}

                    <Image
                        src={arrow}
                        alt="search"
                        width={24}
                        height={24}
                        className={disabled && hasOpenAiKey ? "invisible" : ""}
                    />
                </button>
            </form>
        </>
    );
};

export default InputArea;
